import React, { useState, useEffect } from "react";
import styles from "./Supervisor.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { emptySupervisorStreak, SupervisorStreak } from "../types/supervisor";

const SPECIALTY_CONFIG: Record<string, { name: string; color: string }> = {
    "Endodontics": { name: "Endodontics", color: "#39a3a1" },
    "Prosthodontics": { name: "Prosthodontics", color: "#4cc0be" },
    "Periodontics": { name: "Periodontics", color: "#70cdcb" },
    "Orthodontics": { name: "Orthodontics", color: "#f89b07" },
    "Pediatric Dentistry": { name: "Pediatric", color: "#fbbf60" },
    "Restorative Dentistry": { name: "Restorative", color: "#205b5a" },
    "Oral Medicine": { name: "Oral Medicine", color: "#937663" },
    "OMFS": { name: "OMFS", color: "#ba5656" },
};

interface PieChartProps {
    revealsBySpecialty: Record<string, number>;
    totalRevealed: number;
}

const SpecialtyPieChart: React.FC<PieChartProps> = ({ revealsBySpecialty, totalRevealed }) => {
    const size = 120;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    const sortedData = Object.entries(revealsBySpecialty)
        .map(([specialty, count]) => ({ specialty, count }))
        .sort((a, b) => b.count - a.count);

    let cumulativePercent = 0;
    const segments = sortedData.map((item) => {
        const percent = totalRevealed > 0 ? (item.count / totalRevealed) * 100 : 0;
        const config = SPECIALTY_CONFIG[item.specialty] || { name: item.specialty, color: "#ccc" };
        const segment = {
            ...item,
            percent,
            color: config.color,
            displayName: config.name,
            offset: cumulativePercent,
        };
        cumulativePercent += percent;
        return segment;
    });

    return (
        <div className={styles.pieChartContainer}>
            <div className={styles.pieChartWrapper}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={center} cy={center} r={radius} fill="none" stroke="#ecddcc" strokeWidth={strokeWidth} />
                    {segments.map((seg) => {
                        if (seg.count === 0) return null;
                        const dashLength = (seg.percent / 100) * circumference;
                        const dashOffset = circumference - (seg.offset / 100) * circumference;
                        return (
                            <circle
                                key={seg.specialty}
                                cx={center} cy={center} r={radius}
                                fill="none" stroke={seg.color} strokeWidth={strokeWidth}
                                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                                strokeDashoffset={dashOffset}
                                transform={`rotate(-90 ${center} ${center})`}
                                style={{ transition: "stroke-dasharray 0.5s ease" }}
                            />
                        );
                    })}
                </svg>
                <div className={styles.pieChartCenter}>
                    <span className={styles.pieChartValue}>{totalRevealed}</span>
                    <span className={styles.pieChartLabel}>revealed</span>
                </div>
            </div>
            <div className={styles.pieLegend}>
                {segments.map((seg) => (
                    <div key={seg.specialty} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ backgroundColor: seg.color }} />
                        <span className={styles.legendName}>{seg.displayName}</span>
                        <span className={styles.legendValue}>{seg.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface StudentDetail {
    student_id: string;
    profile: {
        email: string;
        full_name: string;
        created_at: string | null;
    };
    activity: {
        question_count: number;
        message_count: number;
        login_count: number;
        last_login_at: string | null;
    };
    qbank: {
        total_reveals: number;
        total_questions: number;
        progress_pct: number;
        reveals_by_specialty: Record<string, number>;
        collections_progress: Array<{
            user_id: string;
            collection_id: string;
            collection_title: string;
            revealed_count: number;
            total_questions: number;
        }>;
        streak?: SupervisorStreak;
    };
    mock_exams: {
        summary: {
            exams_taken: number;
            avg_score: number;
            best_score: number;
            last_exam_at: string | null;
        };
        history: Array<{
            id: string;
            started_at: string;
            completed_at: string;
            time_taken_seconds: number;
            total_questions: number;
            correct_count: number;
            status: string;
        }>;
    };
}

interface Props {
    studentId: string;
    supervisorId: string;
    onBack: () => void;
    onStudentRemoved: () => void;
}

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

const SupervisorStudentDetail: React.FC<Props> = ({ studentId, supervisorId, onBack, onStudentRemoved }) => {
    const [detail, setDetail] = useState<StudentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        const fetchDetail = async () => {
            setLoading(true);
            try {
                const res = await authenticatedFetch(
                    `${apiUrl}/api/supervisor/students/${studentId}?supervisor_id=${supervisorId}`
                );
                if (res.ok) {
                    setDetail(await res.json());
                } else {
                    const data = await res.json();
                    setError(data.error || "Failed to load student details");
                }
            } catch (err) {
                setError("Failed to load student details");
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [studentId, supervisorId]);

    const handleRemove = async () => {
        setRemoving(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/supervisor/students/${studentId}?supervisor_id=${supervisorId}`,
                { method: "DELETE" }
            );
            if (res.ok) {
                (window as any).showNotification?.("Student removed successfully", "success");
                onStudentRemoved();
                onBack();
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to remove", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to remove student", "error");
        } finally {
            setRemoving(false);
            setConfirmRemove(false);
        }
    };

    const formatDate = (date: string | null) => {
        if (!date) return "Never";
        try {
            return new Date(date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return "Unknown";
        }
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}m ${s}s`;
    };

    const formatStudyDate = (value: string | null) => {
        if (!value) return "Never";
        const [year, month, day] = value.split("-").map(Number);
        if (!year || !month || !day) return value;
        return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    const studyWeekday = (value: string) => {
        const [year, month, day] = value.split("-").map(Number);
        if (!year || !month || !day) return value;
        return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
            weekday: "short",
            timeZone: "UTC",
        });
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading student details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <button className={styles.backButton} onClick={onBack}>
                    &#8592; Back to Students
                </button>
                <div className={styles.section}>
                    <p className={styles.errorText}>{error}</p>
                </div>
            </div>
        );
    }

    if (!detail) return null;

    const { profile, activity, qbank, mock_exams } = detail;
    const streak = qbank.streak ?? emptySupervisorStreak;
    const specialties = Object.entries(qbank.reveals_by_specialty || {}).sort((a, b) => b[1] - a[1]);

    return (
        <div>
            {/* Back button */}
            <button className={styles.backButton} onClick={onBack}>
                &#8592; Back to Students
            </button>

            {/* Student header */}
            <div className={styles.studentHeader}>
                <div className={styles.studentInfo}>
                    <h2>{profile.full_name || "Unnamed Student"}</h2>
                    <div className={styles.studentMeta}>
                        <span>{profile.email}</span>
                        {profile.created_at && <span>Joined {formatDate(profile.created_at)}</span>}
                        <span>Last active: {formatDate(activity.last_login_at)}</span>
                    </div>
                </div>
                <button className={styles.dangerButton} onClick={() => setConfirmRemove(true)}>
                    Remove Student
                </button>
            </div>

            {/* Activity Overview */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Activity</h3>
                <div className={styles.detailStatsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}>Total Logins</div>
                        <div className={styles.statValue}>{activity.login_count}</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}>AI Questions Asked</div>
                        <div className={styles.statValue}>{activity.question_count}</div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statLabel}>Messages Sent</div>
                        <div className={styles.statValue}>{activity.message_count}</div>
                    </div>
                </div>
            </div>

            {/* QBank Progress */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>QBank Progress</h3>

                <div className={styles.streakPanel}>
                    <div className={styles.detailStatsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Current Streak</div>
                            <div className={styles.statValue}>
                                {streak.current_days}<span className={styles.statSuffix}>day{streak.current_days === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Best Streak</div>
                            <div className={styles.statValue}>
                                {streak.longest_days}<span className={styles.statSuffix}>day{streak.longest_days === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Today</div>
                            <div className={styles.streakStatusValue}>
                                {streak.studied_today ? "Practiced" : "Not yet"}
                            </div>
                        </div>
                    </div>

                    {streak.recent_days.length > 0 && (
                        <div className={styles.streakWeek} aria-label="Seven-day study activity">
                            {streak.recent_days.map((studyDay) => (
                                <div
                                    key={studyDay.date}
                                    className={styles.streakDay}
                                    aria-label={`${formatStudyDate(studyDay.date)}: ${studyDay.active ? "studied" : "not studied"}`}
                                >
                                    <span>{studyWeekday(studyDay.date)}</span>
                                    <span className={`${styles.streakDayDot} ${studyDay.active ? styles.streakDayDotActive : ""}`} />
                                </div>
                            ))}
                        </div>
                    )}

                    <p className={styles.streakMeta}>
                        Last studied: {formatStudyDate(streak.last_activity_date)} · Timezone: {streak.timezone.replace(/_/g, " ")}
                    </p>
                </div>

                {/* Overall progress */}
                <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.875rem", color: "var(--Colors-Neutral-600)" }}>
                            Overall: {qbank.total_reveals}/{qbank.total_questions} questions revealed
                        </span>
                        <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--Colors-Primary-700)" }}>
                            {qbank.progress_pct}%
                        </span>
                    </div>
                    <div className={styles.progressBar} style={{ maxWidth: "none", height: "8px" }}>
                        <div
                            className={styles.progressFill}
                            style={{ width: `${Math.min(qbank.progress_pct, 100)}%` }}
                        />
                    </div>
                </div>

                {/* Per-collection breakdown */}
                {qbank.collections_progress.length > 0 && (
                    <div style={{ marginBottom: "1.5rem" }}>
                        <h4 style={{ fontSize: "0.9375rem", fontWeight: 600, color: "var(--Colors-Primary-800)", marginBottom: "0.75rem" }}>
                            By Collection
                        </h4>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Collection</th>
                                    <th>Revealed</th>
                                    <th>Total</th>
                                    <th>Progress</th>
                                </tr>
                            </thead>
                            <tbody>
                                {qbank.collections_progress.map((c) => {
                                    const pct = c.total_questions > 0 ? Math.round((c.revealed_count / c.total_questions) * 100) : 0;
                                    return (
                                        <tr key={c.collection_id}>
                                            <td>{c.collection_title}</td>
                                            <td>{c.revealed_count}</td>
                                            <td>{c.total_questions}</td>
                                            <td>
                                                <div className={styles.progressBarContainer}>
                                                    <div className={styles.progressBar}>
                                                        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className={styles.progressText}>{pct}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Per-specialty breakdown with pie chart */}
                {specialties.length > 0 && (
                    <div>
                        <h4 style={{ fontSize: "0.9375rem", fontWeight: 600, color: "var(--Colors-Primary-800, #184342)", marginBottom: "0.75rem" }}>
                            By Specialty
                        </h4>
                        <SpecialtyPieChart
                            revealsBySpecialty={qbank.reveals_by_specialty}
                            totalRevealed={qbank.total_reveals}
                        />
                    </div>
                )}

                {qbank.total_reveals === 0 && (
                    <p style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.875rem" }}>
                        No QBank questions revealed yet.
                    </p>
                )}
            </div>

            {/* Mock Exams */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Mock Exams</h3>

                {mock_exams.summary.exams_taken > 0 ? (
                    <>
                        <div className={styles.detailStatsGrid} style={{ marginBottom: "1.5rem" }}>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Exams Taken</div>
                                <div className={styles.statValue}>{mock_exams.summary.exams_taken}</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Average Score</div>
                                <div className={styles.statValue}>
                                    {mock_exams.summary.avg_score}<span className={styles.statSuffix}>%</span>
                                </div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Best Score</div>
                                <div className={styles.statValue}>
                                    {mock_exams.summary.best_score}<span className={styles.statSuffix}>%</span>
                                </div>
                            </div>
                        </div>

                        <h4 style={{ fontSize: "0.9375rem", fontWeight: 600, color: "var(--Colors-Primary-800)", marginBottom: "0.75rem" }}>
                            Exam History
                        </h4>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Score</th>
                                    <th>Percentage</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mock_exams.history.map((exam) => {
                                    const pct = exam.total_questions > 0
                                        ? Math.round((exam.correct_count / exam.total_questions) * 100)
                                        : 0;
                                    return (
                                        <tr key={exam.id}>
                                            <td>{formatDate(exam.completed_at)}</td>
                                            <td>{exam.correct_count}/{exam.total_questions}</td>
                                            <td>{pct}%</td>
                                            <td>{formatTime(exam.time_taken_seconds)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                ) : (
                    <p style={{ color: "var(--Colors-Neutral-500)", fontSize: "0.875rem" }}>
                        No mock exams taken yet.
                    </p>
                )}
            </div>

            {/* Remove confirmation modal */}
            {confirmRemove && (
                <div className={styles.modalOverlay} onClick={() => setConfirmRemove(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3>Remove Student</h3>
                        <p>
                            Are you sure you want to remove <strong>{profile.full_name || profile.email}</strong>?
                            If they have a supervisor-granted subscription, it will be revoked.
                        </p>
                        <div className={styles.modalActions}>
                            <button className={styles.secondaryButton} onClick={() => setConfirmRemove(false)}>
                                Cancel
                            </button>
                            <button className={styles.dangerButton} onClick={handleRemove} disabled={removing}>
                                {removing ? "Removing..." : "Remove"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupervisorStudentDetail;
